import type {
    GridDataSource,
    GridGetRowsParams,
    GridGetRowsResponse,
    GridRowModel,
    GridUpdateRowParams
} from "@mui/x-data-grid-premium";
import type {CategorizationReviewStore} from "~/stores/categorization-review/CategorizationReviewStore.ts";
import type {
    MatPartReviewRow,
    MatReviewAggregatedGroupData,
} from "~/services/classes/MaterializedClasses.ts";
import type {MuiXGroupedDataResponse} from "~/components/muix-data-grid/MuixApiTypes.ts";


export type AsyncGridRowModel = GridRowModel & {
    /**
     * Used to indicate if the row is loading / being updated
     */
    isLoading: boolean
    /**
     * Used to indicate if the row is out of sync with the server
     */
    isOutOfSync?: boolean
    /**
     * A hotfix to ensure the interactions that require the tree to be in sync do not happen in this group (Open/Close / Selection)
     *
     * CAT-3005: Due to missing functionality of MuiX Data Grid we cannot update children rows
     *  without calling the API again. (See GridRowProApi[setRowChildrenExpansion])
     *
     * The parent will be updated by setRows(). This importunately automatically sets the state to 'closed' in MuiX.
     */
    hotfixV8DisableInteractions?: boolean
}

export type PartGridRowModel = AsyncGridRowModel
    & MatPartReviewRow // Flattened
    & {
    /**
     * Is true for the last row in a subgroup when there are more rows available
     * Used to display a subgroup pagination
     */
    subGroupPagination?: boolean
}

export type PartGridGroupModel = AsyncGridRowModel
    & MuiXGroupedDataResponse<MatReviewAggregatedGroupData>

export type Row = (PartGridRowModel | PartGridGroupModel) & {id: string};

/**
 * Points to the field used for selecting rows in the group
 * For Grouped and Rows responses we must have: r[GROUP_KEY] === g.id
 */
export const GROUP_KEY = 'supplier_id' satisfies keyof MatPartReviewRow
/**
 * Points to the field shown as group
 * For Grouped responses we must have: r[GROUP_VAL] === g.group
 */
export const GROUP_VAL = 's_name' satisfies keyof MatPartReviewRow
/**
 * Sortable fields when in group mode
 */
export const GROUP_SORTABLE_KEYS: string[] = ['p_spend']
// GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD = __row_group_by_columns_group__

/**
 * DataModel as according to MuiX Data Grid specifications
 */
export const getCategorizationReviewTableDataModel: (databag: number, revision: number | undefined, categorizationReviewStore: CategorizationReviewStore) => GridDataSource = (databag, revision, categorizationReviewStore) => {
    const reviewPageSelectionController = categorizationReviewStore.reviewPageController.reviewPageSelectionController;
    const filterController = categorizationReviewStore.reviewPageController.reviewPageFilterController;
    const reviewPageController = categorizationReviewStore.reviewPageController;
    const reviewPageTableController = categorizationReviewStore.reviewPageController.reviewPageTableController;
    const reviewApi = categorizationReviewStore.reviewPageController.reviewApi;

    return {
        getRows: async (params: GridGetRowsParams): Promise<GridGetRowsResponse> => {
            // Store the related fields at time of query
            const relatedData = reviewPageController.relatedDataFieldsSpec;

            // Grouping
            // noinspection TypeScriptValidateTypes
            const group_by = params.groupFields?.at(0) ?? null;
            // noinspection TypeScriptValidateTypes
            const group_key = params.groupKeys?.at(0) ?? null;
            // Page options would be set on reviewApi.get
            const response = await reviewApi.get(databag, revision, params, group_by, group_key, filterController.apiGridFilterModel, relatedData);
            // When no error happens, clear error messages
            reviewPageController.setPaginationStateInvalid(false);
            reviewPageController.setPaginationStateInvalidSupplierId(undefined);
            return reviewPageTableController.transformGetRows(response.data, group_by, group_key, relatedData);
        },

        getGroupKey(row: GridRowModel): string {
            // We don't have access to the group_by field, so we need to use a fixed field here
            return String(row.id || String(row.part));
        },

        getChildrenCount(row: GridRowModel): number {
            return row.count;
        },

        getAggregatedValue: function (row: GridRowModel, field: string) {
            // console.debug('getTableDataModel.getAggregatedValue', row, field);
            // For last row in group by mode, we don't want aggregation to be displayed
            if (row.id === undefined && row.group === undefined)
                return null;
            return row[`${field}_agg`];
        },

        updateRow: async function (_params: GridUpdateRowParams) {
            // We do not use the edit function of MuiX Data Grid
            throw new Error('Not implemented');
        }

    } as const satisfies GridDataSource;
}
